// Data-driven navigation configuration for the Payroll UI left-hand navigation.
//
// The navigation tree has two structural levels rendered in the sidebar:
//   - Section  -> a non-navigating grouping/expansion node (Mango NavigationGroup).
//                 The "Home" section is the single exception: it is a direct
//                 destination with no child items.
//   - Item     -> a navigating destination (Mango NavigationItem) that routes to a page.
//
// A third conceptual level, Sub-item, is rendered as Mango tabs on the Item page
// (see TabbedItemPage). Sub-item tabs are URL-addressable: one route per tab.
//
// Future screens replace placeholders by changing a single `content` key here; the
// navigation structure and routing do not need to be restructured.

/** Identifies which screen renders for a destination. Defaults to a reusable placeholder. */
export type ContentKey = 'placeholder' | 'databaseConnectivity' | 'organisationDetails';

export interface NavSubItem {
  label: string;
  /** kebab-case slug derived from the label; the final URL segment for this tab. */
  slug: string;
  content: ContentKey;
}

export interface NavItem {
  label: string;
  /** kebab-case slug derived from the label; the URL segment for this item. */
  slug: string;
  /** Optional contextual note carried from the ticket (e.g. "Read-only panel"). */
  note?: string;
  /** When present, this item renders a tab strip; tabs are URL-addressable. */
  subItems?: NavSubItem[];
  /** Destination for a leaf item (no sub-items). Defaults to placeholder. */
  content?: ContentKey;
}

export interface NavSection {
  label: string;
  /** kebab-case slug; the first URL segment for items in this section. */
  slug: string;
  /** Mango icon name for the section header / direct item. */
  icon: string;
  /** Present only for the Home section: a direct destination, no child items. */
  home?: { content: ContentKey };
  /** Present for grouping sections. */
  items?: NavItem[];
}

/** Convert a human label into a stable kebab-case slug used in URLs. */
export const slugify = (label: string): string =>
  label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sub = (label: string, content: ContentKey = 'placeholder'): NavSubItem => ({ label, slug: slugify(label), content });
const item = (label: string, opts: { note?: string; content?: ContentKey; subItems?: NavSubItem[] } = {}): NavItem => ({
  label,
  slug: slugify(label),
  ...opts,
});

export const navSections: NavSection[] = [
  {
    label: 'Home',
    slug: '',
    icon: 'home',
    home: { content: 'databaseConnectivity' },
  },
  {
    label: 'Workers',
    slug: 'workers',
    icon: 'person',
    items: [
      item('Worker List'),
      item('Worker Record', {
        note: 'Tab strip',
        subItems: [
          'Overview', 'Personal', 'Person Details', 'Contacts', 'Address', 'Emergency',
          'Employment', 'ER', 'Tax', 'Banking', 'Super', 'Work Assignments', 'Pay & Tax',
          'Leave', 'Documents', 'Notes', 'Activity',
        ].map((l) => sub(l)),
      }),
      item('Termination Details', { note: 'Read-only panel' }),
      item('Employee Processes', {
        subItems: ['Onboarding', 'Workforce Sync', 'Bulk Pay Update', 'Compliance Tasks'].map((l) => sub(l)),
      }),
    ],
  },
  {
    label: 'Payroll',
    slug: 'payroll',
    icon: 'payroll-dollar',
    items: [
      item('Pay Runs', { subItems: ['Transactions', 'Compliance Notifications'].map((l) => sub(l)) }),
      item('STP Management', {
        note: 'Operational',
        subItems: ['Management', 'Reconciliation', 'Pay Events', 'Update Events', 'Finalisation', 'Full File Replacement'].map((l) => sub(l)),
      }),
    ],
  },
  {
    label: 'General Ledger',
    slug: 'general-ledger',
    icon: 'chart',
    items: [
      item('Overview'),
      item('Cost Centre'),
      item('Chart of Accounts'),
      item('Formulas'),
      item('Factors'),
      item('Rule'),
      item('Export'),
      item('Reporting'),
    ],
  },
  {
    label: 'Maintenance',
    slug: 'maintenance',
    icon: 'settings',
    items: [
      item('Organisation', {
        subItems: [
          sub('Overview'),
          sub('Business Records', 'organisationDetails'),
          sub('Payrolls & Calendars'),
          sub('EFT'),
          sub('Compliance Rules'),
          sub('Regionalisation'),
          sub('Employer Levy Registration'),
          sub('Public Holidays'),
          sub('Payslip Templates'),
        ],
      }),
      item('STP Setup', {
        subItems: ['ATO Connection', 'Declarers', 'Payer Details', 'Income Types', 'Tax Treatments', 'Disaggregation Map', 'Notifications'].map((l) => sub(l)),
      }),
      item('Configuration', {
        subItems: [
          'Awards & Classifications', 'Overview', 'Rates', 'Reviews', 'Pay Types Assigned',
          'History', 'Pay Templates', 'Work Schedules', 'Pay Types', 'Tax', 'Banking',
        ].map((l) => sub(l)),
      }),
      item('Leave', { subItems: ['Leave Types', 'Leave Type Templates'].map((l) => sub(l)) }),
      item('Superannuation', {
        subItems: ['Super Funds', 'Global SG Rules', 'Contributions Dashboard', 'Super Business Logic'].map((l) => sub(l)),
      }),
    ],
  },
  {
    label: 'Administration',
    slug: 'administration',
    icon: 'lock',
    items: [
      item('Users & Access'),
      item('Security Roles'),
      item('Approval Workflows'),
      item('General Parameters'),
      item('Integration Settings'),
      item('Import Templates & Opening Balances'),
      item('Payment Setup'),
      item('Payroll Notes', { note: 'Renamed from "Significant Date Types"' }),
      item('Audit Log'),
      item('Alerts & Notifications'),
    ],
  },
];

/** Absolute route path for the Home destination. */
export const HOME_PATH = '/';

/** Absolute route path for an item (without leading slash collisions). */
export const itemPath = (section: NavSection, navItem: NavItem): string => `/${section.slug}/${navItem.slug}`;

/** Absolute route path for a sub-item tab. */
export const subItemPath = (section: NavSection, navItem: NavItem, subItem: NavSubItem): string =>
  `${itemPath(section, navItem)}/${subItem.slug}`;

export interface ResolvedItem {
  section: NavSection;
  item: NavItem;
  path: string;
  /** First sub-item slug, when the item is tabbed. */
  firstSubSlug?: string;
}

/** Resolve a navigating item from its absolute route path (e.g. "/workers/worker-record"). */
export const resolveItemByPath = (path: string): ResolvedItem | undefined =>
  flattenItems().find((resolved) => resolved.path === path);

/** Every navigating item (excludes Home, which is handled separately). */
export const flattenItems = (): ResolvedItem[] =>
  navSections.flatMap((section) =>
    (section.items ?? []).map((navItem) => ({
      section,
      item: navItem,
      path: itemPath(section, navItem),
      firstSubSlug: navItem.subItems?.[0]?.slug,
    })),
  );

export interface ResolvedDestination {
  path: string;
  content: ContentKey;
  title: string;
}

/** Every concrete destination route (Home, leaf items, and each sub-item tab) with its content + title. */
export const flattenDestinations = (): ResolvedDestination[] => {
  const destinations: ResolvedDestination[] = [];
  for (const section of navSections) {
    if (section.home) {
      destinations.push({ path: HOME_PATH, content: section.home.content, title: section.label });
      continue;
    }
    for (const navItem of section.items ?? []) {
      if (navItem.subItems?.length)
        for (const subItem of navItem.subItems)
          destinations.push({ path: subItemPath(section, navItem, subItem), content: subItem.content, title: `${navItem.label} — ${subItem.label}` });
      else destinations.push({ path: itemPath(section, navItem), content: navItem.content ?? 'placeholder', title: navItem.label });
    }
  }
  return destinations;
};
